/**
 * RegionBuilder — compose a region/subregion from geographies (BON-153),
 * Nominatim area picker + a Mapbox map of the region's area. Pick a level
 * (state/county/city/neighborhood), search, add; the map shows the saved
 * boundary plus the areas being added. Save runs behind the confirm-before-save
 * reassignment review. Route: /admin/regions/:id/edit.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { AreaSearchResult, GeoType } from '../../../../api/regions';
import { BucketError, BucketLoading } from '../../regional/BucketStates';

import { BuilderComposePanel } from './BuilderComposePanel';
import { BuilderHeader } from './BuilderHeader';
import { BuilderMapPanel } from './BuilderMapPanel';
import { ReassignConfirmModal } from './ReassignConfirmModal';
import {
  useRegionDetail,
  useRegionComponents,
  useRegionBoundary,
  useMapVenues,
} from './regionsQueries';
import { useComposition } from './useComposition';
import { useMapAreaAdd } from './useMapAreaAdd';

export default function RegionBuilder() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const detailQ = useRegionDetail(id);
  const componentsQ = useRegionComponents(id);
  const boundaryQ = useRegionBoundary(id);
  const venuesQ = useMapVenues();
  const composer = useComposition(id, componentsQ.data);
  const [preview, setPreview] = useState<AreaSearchResult | null>(null);
  // Shared geography level for the typeahead and the map's click-to-add.
  const [level, setLevel] = useState<GeoType>('city');
  const [addMode, setAddMode] = useState(false);
  const mapAdd = useMapAreaAdd(id, level, composer.addComp);

  // Areas added this session carry their polygon — render them over the saved boundary.
  const draftAreas = useMemo(
    () => composer.added.map((c) => c.geojson).filter(Boolean),
    [composer.added],
  );

  if (detailQ.isLoading || componentsQ.isLoading) return <BucketLoading />;
  if (detailQ.isError) return <BucketError error={detailQ.error} />;
  const detail = detailQ.data;
  if (!detail) return <BucketError error={detailQ.error} />;

  const regionName = detail.name;
  const back = () => navigate('/admin/regions');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
      <BuilderHeader
        regionName={regionName}
        compCount={composer.comps.length}
        addedCount={composer.added.length}
        error={composer.error}
        onCancel={back}
        onSave={() => void composer.openConfirm()}
      />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)',
          gap: 14,
        }}
      >
        <BuilderMapPanel
          boundary={boundaryQ.data ?? null}
          draftAreas={draftAreas}
          preview={preview?.geojson ?? null}
          color={detail.color ?? '#059669'}
          venues={venuesQ.data ?? []}
          addMode={addMode}
          onMapClick={(lat, lon) => void mapAdd.onMapClick(lat, lon)}
        />

        <BuilderComposePanel
          regionId={id}
          name={detail.name}
          directorUserId={detail.directorUserId}
          composer={composer}
          onHover={setPreview}
          level={level}
          onLevelChange={setLevel}
          addMode={addMode}
          onToggleAddMode={() => setAddMode((v) => !v)}
          mapMessage={mapAdd.message}
        />
      </div>

      {composer.affected !== null && (
        <ReassignConfirmModal
          toRegionName={regionName}
          affected={composer.affected}
          saving={composer.saving}
          onConfirm={() => void composer.save(back)}
          onCancel={composer.closeConfirm}
        />
      )}
    </div>
  );
}
